/**
 * Translates the inline CSS our block renderers emit into Forme's style model.
 *
 * BroadPaper blocks produce a deliberately small, self-generated CSS vocabulary
 * (we never accept CSS from users), which is what makes a faithful mapping
 * possible at all. Anything outside that vocabulary is reported rather than
 * dropped silently — the caller surfaces it as a validation warning.
 */

/** CSS pixels at 96 dpi → PDF points at 72 dpi. */
export const PX_TO_PT = 72 / 96;

export function pxToPt(px: number): number {
  return Math.round(px * PX_TO_PT * 1000) / 1000;
}

export interface FormeStyle {
  [prop: string]: unknown;
}

export interface StyleTranslation {
  style: FormeStyle;
  /** CSS declarations with no Forme equivalent, e.g. "box-shadow". */
  unsupported: string[];
}

/** Parses an inline style string or object into kebab-case declarations. */
export function parseDeclarations(style: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!style) return out;
  if (typeof style === "object") {
    for (const [k, v] of Object.entries(style as Record<string, unknown>)) {
      if (v === undefined || v === null || v === "") continue;
      out[kebab(k)] = String(v);
    }
    return out;
  }
  for (const decl of String(style).split(";")) {
    const i = decl.indexOf(":");
    if (i < 0) continue;
    const k = decl.slice(0, i).trim();
    const v = decl.slice(i + 1).trim();
    if (k && v) out[k] = v;
  }
  return out;
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

/** "12px" → 9 (pt); "50%" → "50%"; "auto" → undefined. */
export function length(value: string): number | string | undefined {
  const v = value.trim();
  if (v === "auto" || v === "none" || v === "inherit") return undefined;
  if (v.endsWith("%")) return v;
  const n = Number.parseFloat(v);
  if (!Number.isFinite(n)) return undefined;
  if (v.endsWith("em")) return undefined; // resolved against font size we do not track here
  return pxToPt(n);
}

function num(value: string): number | undefined {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * The `flex` shorthand, expanded — all three terms, not just the grow one.
 *
 * Reading only the first term is how `flex: 0 0 40%` became "do not grow" with
 * no basis at all, so a key/value list's label sized itself to its own text
 * instead of taking 40% of the row. That left the value beside it wider in the
 * file than in the browser, and a line that wrapped on the canvas fitted on one
 * line in the PDF: a section measured a line shorter than it draws, which
 * preview then crops. A field added to a section was enough to lose its last
 * row, and the section tolerance in `measure-parity.test.ts` was wide enough to
 * let a sixteen-point disagreement sit there unnoticed.
 *
 * The omitted-term defaults are CSS's own: a bare number is `<n> 1 0%`, a bare
 * basis is `1 1 <basis>`. `auto`, `none` and `initial` leave the basis unset,
 * which is the engine's automatic sizing and what those keywords mean.
 */
function flexShorthand(value: string): { grow?: number; shrink?: number; basis?: number | string } {
  const v = value.trim().toLowerCase();
  if (v === "none") return { grow: 0, shrink: 0 };
  if (v === "auto") return { grow: 1, shrink: 1 };
  if (v === "initial") return { grow: 0, shrink: 1 };

  const parts = v.split(/\s+/).filter(Boolean);
  const isCount = (p: string) => /^-?(\d+\.?\d*|\.\d+)$/.test(p);
  const [a, b, c] = parts;
  if (!a) return {};
  if (parts.length === 1) return isCount(a) ? { grow: num(a), shrink: 1, basis: 0 } : { grow: 1, shrink: 1, basis: length(a) };
  if (parts.length === 2) {
    return isCount(b!) ? { grow: num(a), shrink: num(b!), basis: 0 } : { grow: num(a), shrink: 1, basis: length(b!) };
  }
  return { grow: num(a), shrink: num(b!), basis: length(c!) };
}

/** Normalises a CSS colour to `#rrggbb` / `rgba()` that Forme's parser accepts. */
export function colour(value: string): string | undefined {
  const v = value.trim().toLowerCase();
  if (!v || v === "transparent" || v === "none" || v === "inherit" || v === "currentcolor") return undefined;
  return v;
}

const EDGE_ORDER = ["top", "right", "bottom", "left"] as const;

/** "4px 8px" → { top: 3, right: 6, bottom: 3, left: 6 } in points. */
export function edges(value: string): { top: number; right: number; bottom: number; left: number } | undefined {
  const parts = value.trim().split(/\s+/).map((p) => length(p));
  if (parts.some((p) => typeof p !== "number")) return undefined;
  const n = parts as number[];
  const [t, r, b, l] = n.length === 1 ? [n[0]!, n[0]!, n[0]!, n[0]!] : n.length === 2 ? [n[0]!, n[1]!, n[0]!, n[1]!] : n.length === 3 ? [n[0]!, n[1]!, n[2]!, n[1]!] : [n[0]!, n[1]!, n[2]!, n[3]!];
  return { top: t, right: r, bottom: b, left: l };
}

/** "1px solid #E2E5EA" → { width, style, colour }. */
export function border(value: string): { width: number; colour?: string } | undefined {
  const v = value.trim();
  if (!v || v === "none" || v.startsWith("0 ") || v === "0") return undefined;
  const widthMatch = /(-?[\d.]+)px/.exec(v);
  const colourMatch = /(#[0-9a-f]{3,8}|rgba?\([^)]+\))/i.exec(v);
  const width = widthMatch ? pxToPt(Number(widthMatch[1])) : 0.75;
  if (/\bnone\b/.test(v)) return undefined;
  return { width, colour: colourMatch ? colourMatch[0] : undefined };
}

const FLEX_DIRECTION: Record<string, string> = { row: "row", column: "column", "row-reverse": "row-reverse", "column-reverse": "column-reverse" };
const JUSTIFY: Record<string, string> = { "flex-start": "flex-start", start: "flex-start", "flex-end": "flex-end", end: "flex-end", center: "center", "space-between": "space-between", "space-around": "space-around", "space-evenly": "space-evenly" };
const ALIGN: Record<string, string> = { "flex-start": "flex-start", start: "flex-start", "flex-end": "flex-end", end: "flex-end", center: "center", stretch: "stretch", baseline: "baseline" };
const TEXT_ALIGN: Record<string, string> = { left: "left", right: "right", center: "center", justify: "justify" };

/** Declarations we knowingly ignore because they have no bearing on PDF output. */
const IGNORED = new Set([
  "box-sizing", "-webkit-font-smoothing", "text-rendering", "font-variant-numeric", "pointer-events",
  "user-select", "cursor", "text-overflow", "white-space", "word-wrap", "overflow-wrap", "box-decoration-break",
  "-webkit-box-decoration-break", "text-underline-offset", "text-decoration-thickness", "outline", "transition",
  "list-style", "border-collapse", "border-spacing", "table-layout", "vertical-align", "min-width", "max-width",
  "-webkit-line-clamp", "-webkit-box-orient", "object-fit", "content", "zoom", "transform-origin"
]);

/**
 * Maps CSS declarations to Forme style props. Returns the props plus any
 * declaration we could not represent.
 */
export function translateStyle(css: Record<string, string>): StyleTranslation {
  const s: FormeStyle = {};
  const unsupported: string[] = [];
  const set = (k: string, v: unknown) => {
    if (v !== undefined) s[k] = v;
  };

  for (const [prop, raw] of Object.entries(css)) {
    const value = raw.trim();
    if (IGNORED.has(prop)) continue;
    switch (prop) {
      case "display":
        if (value === "flex" || value === "inline-flex") set("display", "flex");
        else if (value === "grid") {
          // Our only grids are equal-track column layouts; flex row reproduces
          // them exactly and is honoured by the engine. Whether the row wraps
          // depends on the child count, which the translator decides.
          set("display", "flex");
          set("flexDirection", "row");
        } else if (value === "none") set("display", "none");
        break;
      case "flex-direction":
        set("flexDirection", FLEX_DIRECTION[value]);
        break;
      case "justify-content":
        set("justifyContent", JUSTIFY[value]);
        break;
      case "align-items":
        set("alignItems", ALIGN[value]);
        break;
      case "align-self":
        set("alignSelf", ALIGN[value]);
        break;
      case "flex-wrap":
        set("flexWrap", value === "wrap" ? "wrap" : "nowrap");
        break;
      case "flex": {
        const f = flexShorthand(value);
        set("flexGrow", f.grow);
        set("flexShrink", f.shrink);
        set("flexBasis", f.basis);
        break;
      }
      case "flex-grow":
        set("flexGrow", num(value));
        break;
      case "flex-shrink":
        set("flexShrink", num(value));
        break;
      case "gap":
      case "row-gap":
      case "column-gap": {
        const parts = value.split(/\s+/).map((p) => length(p));
        const first = typeof parts[0] === "number" ? parts[0] : undefined;
        const second = parts.length > 1 && typeof parts[1] === "number" ? parts[1] : first;
        if (prop === "gap") {
          set("rowGap", first);
          set("columnGap", second);
        } else set(prop === "row-gap" ? "rowGap" : "columnGap", first);
        break;
      }
      case "grid-template-columns":
        // Track count is recovered by the translator, which turns our 12-track
        // rows into proportional flex children.
        break;
      case "grid-column": {
        // "span 6" of 12 tracks: grow in proportion to the span.
        const span = /^span\s+(\d+)$/.exec(value);
        if (span) {
          set("flexGrow", Number(span[1]));
          set("flexBasis", 0);
        }
        break;
      }
      case "width":
        set("width", length(value));
        break;
      case "height": {
        // A percentage height is a CSS-ism for "fill the row"; in a flow layout
        // it has no meaningful referent, and the engine resolves it to points,
        // which would inflate every row it appears on. Flex stretch covers it.
        const h = length(value);
        if (typeof h === "number") set("height", h);
        break;
      }
      case "padding":
        set("padding", edges(value));
        break;
      case "padding-top":
      case "padding-right":
      case "padding-bottom":
      case "padding-left":
        set(`padding${cap(prop.slice(8))}`, length(value));
        break;
      case "margin":
        set("margin", edges(value));
        break;
      case "margin-top":
      case "margin-right":
      case "margin-bottom":
      case "margin-left":
        set(`margin${cap(prop.slice(7))}`, length(value));
        break;
      case "background":
      case "background-color":
        set("backgroundColor", colour(value));
        break;
      case "color":
        set("color", colour(value));
        break;
      case "font-family":
        set("fontFamily", value.split(",")[0]!.trim().replace(/^['"]|['"]$/g, ""));
        break;
      case "font-size":
        set("fontSize", length(value));
        break;
      case "font-weight":
        set("fontWeight", value === "bold" ? 700 : value === "normal" ? 400 : num(value));
        break;
      case "font-style":
        if (value === "italic") set("fontStyle", "italic");
        break;
      case "line-height":
        set("lineHeight", num(value));
        break;
      case "letter-spacing":
        if (value.endsWith("px")) set("letterSpacing", length(value));
        break;
      case "text-align":
        set("textAlign", TEXT_ALIGN[value]);
        break;
      case "text-transform":
        if (value !== "none") set("textTransform", value);
        break;
      case "text-decoration":
        if (value.includes("underline")) set("textDecoration", "underline");
        else if (value.includes("line-through")) set("textDecoration", "line-through");
        break;
      case "opacity":
        set("opacity", num(value));
        break;
      case "overflow":
        if (value === "hidden") set("overflow", "hidden");
        break;
      case "border-radius": {
        const r = length(value.split(/\s+/)[0] ?? "0");
        set("borderRadius", typeof r === "number" ? r : undefined);
        break;
      }
      case "border": {
        const b = border(value);
        if (b) {
          set("borderWidth", b.width);
          set("borderColor", b.colour);
        }
        break;
      }
      case "border-top":
      case "border-right":
      case "border-bottom":
      case "border-left": {
        const side = cap(prop.slice(7));
        const b = border(value);
        if (b) {
          set(`border${side}Width`, b.width);
          set(`border${side}Color`, b.colour);
        }
        break;
      }
      case "position":
        // Relative matters as much as absolute: it is what makes a box the
        // frame its absolutely positioned children are measured from.
        if (value === "absolute" || value === "relative") set("position", value);
        break;
      case "top":
      case "left":
      case "right":
      case "bottom":
        set(prop, length(value));
        break;
      default:
        unsupported.push(prop);
    }
  }
  // CSS defaults `flex-direction` to row; the engine's React-Native-flavoured
  // model defaults to column. Without this every unqualified `display: flex`
  // silently stacks — the single largest source of layout drift we found.
  if (s.display === "flex" && s.flexDirection === undefined) s.flexDirection = "row";
  return { style: s, unsupported };
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
